/* ============================================================
   TerraLife — Recherche blog (composant isolé)
   Custom element <tl-blog-search> : suggestions d'articles en temps réel
   via l'API Predictive Search native de Shopify (titre + contenu).
   Aucune dépendance au JS de Glozin.
   ============================================================ */
(() => {
  if (customElements.get('tl-blog-search')) return;

  const esc = (s) =>
    String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));

  const stripHtml = (html) => {
    const d = document.createElement('div');
    d.innerHTML = html || '';
    return (d.textContent || '').replace(/\s+/g, ' ').trim();
  };

  const sizeImg = (url, w) => (url ? url + (url.includes('?') ? '&' : '?') + 'width=' + w : '');

  const highlight = (text, q) => {
    const t = esc(text);
    const words = (q || '')
      .split(/\s+/)
      .filter((w) => w.length >= 2)
      .map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    if (!words.length) return t;
    return t.replace(new RegExp('(' + words.join('|') + ')', 'gi'), '<mark>$1</mark>');
  };

  class TLBlogSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('.tl-bsearch-input');
      this.panel = this.querySelector('.tl-bsearch-panel');
      this.list = this.querySelector('.tl-bsearch-list');
      this.footer = this.querySelector('.tl-bsearch-footer');
      this.clearBtn = this.querySelector('.tl-bsearch-clear');
      this.form = this.querySelector('form');
      if (!this.input || !this.panel || !this.list) return;

      this.endpoint = this.dataset.endpoint || '/search/suggest.json';
      this.resultsUrl = this.dataset.resultsUrl || '/search';
      this.limit = parseInt(this.dataset.limit || '6', 10);
      this.minChars = parseInt(this.dataset.minChars || '2', 10);
      this.showExcerpt = this.dataset.showExcerpt === 'true';
      this.showImage = this.dataset.showImage === 'true';

      this.cache = new Map();
      this.controller = null;
      this.timer = null;
      this.items = [];
      this.activeIndex = -1;

      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.input.addEventListener('focus', () => { if (this.items.length) this.open(); });
      this.clearBtn && this.clearBtn.addEventListener('click', () => this.clear());
      this.form.addEventListener('submit', (e) => {
        if (this.input.value.trim().length === 0) e.preventDefault();
      });
      this._onDocClick = (e) => { if (!this.contains(e.target)) this.close(); };
      document.addEventListener('click', this._onDocClick);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
    }

    onInput() {
      const q = this.input.value.trim();
      if (this.clearBtn) this.clearBtn.hidden = q.length === 0;
      clearTimeout(this.timer);
      if (q.length < this.minChars) { this.items = []; this.close(); return; }
      this.timer = setTimeout(() => this.search(q), 250);
    }

    async search(q) {
      if (this.cache.has(q)) { this.render(q, this.cache.get(q)); return; }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.classList.add('is-loading');

      const params = new URLSearchParams({
        q,
        'resources[type]': 'article',
        'resources[limit]': String(this.limit),
        'resources[options][fields]': 'title,body'
      });

      try {
        const res = await fetch(`${this.endpoint}?${params.toString()}`, {
          signal: this.controller.signal,
          headers: { Accept: 'application/json' }
        });
        if (!res.ok) throw new Error('HTTP ' + res.status);
        const data = await res.json();
        const articles =
          (data && data.resources && data.resources.results && data.resources.results.articles) || [];
        this.cache.set(q, articles);
        // Ne rend que si la requête est toujours d'actualité
        if (this.input.value.trim() === q) this.render(q, articles);
      } catch (err) {
        if (err.name !== 'AbortError') this.renderError();
      } finally {
        this.classList.remove('is-loading');
      }
    }

    render(q, articles) {
      this.items = articles;
      this.list.innerHTML = '';
      this.footer.innerHTML = '';
      this.activeIndex = -1;

      if (!articles.length) {
        this.list.innerHTML = `<li class="tl-bsearch-empty">Aucun guide trouvé pour « ${esc(q)} »</li>`;
        this.open();
        return;
      }

      const frag = document.createDocumentFragment();
      articles.forEach((a, i) => {
        const li = document.createElement('li');
        li.className = 'tl-bsearch-item';
        li.setAttribute('role', 'option');
        li.setAttribute('aria-selected', 'false');
        li.id = `${this.list.id}-opt-${i}`;

        const thumb = this.showImage && a.image
          ? `<img class="tl-bsearch-thumb" src="${esc(sizeImg(a.image, 160))}" alt="" loading="lazy" width="56" height="56">`
          : `<span class="tl-bsearch-thumb tl-bsearch-thumb--ph" aria-hidden="true"></span>`;

        const excerpt = this.showExcerpt
          ? `<span class="tl-bsearch-excerpt">${esc(stripHtml(a.summary_html).slice(0, 140))}</span>`
          : '';

        li.innerHTML =
          `<a class="tl-bsearch-link" href="${esc(a.url)}">${thumb}` +
          `<span class="tl-bsearch-text"><span class="tl-bsearch-title">${highlight(a.title, q)}</span>${excerpt}</span></a>`;

        li.addEventListener('mouseenter', () => { this.activeIndex = i; this.updateActive(); });
        frag.appendChild(li);
      });
      this.list.appendChild(frag);

      const all = document.createElement('a');
      all.className = 'tl-bsearch-all';
      all.href = `${this.resultsUrl}?q=${encodeURIComponent(q)}&type=article`;
      all.textContent = 'Voir tous les résultats';
      this.footer.appendChild(all);

      this.open();
    }

    renderError() {
      this.items = [];
      this.list.innerHTML = '<li class="tl-bsearch-empty">Recherche indisponible pour le moment.</li>';
      this.footer.innerHTML = '';
      this.open();
    }

    updateActive() {
      const lis = Array.from(this.list.querySelectorAll('.tl-bsearch-item'));
      lis.forEach((li, i) => {
        const on = i === this.activeIndex;
        li.classList.toggle('is-active', on);
        li.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      const cur = lis[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', cur ? cur.id : '');
      if (cur) cur.scrollIntoView({ block: 'nearest' });
    }

    onKey(e) {
      const n = this.items.length;
      if (e.key === 'ArrowDown' && n) {
        e.preventDefault();
        if (this.panel.hidden) this.open();
        this.activeIndex = (this.activeIndex + 1) % n;
        this.updateActive();
      } else if (e.key === 'ArrowUp' && n) {
        e.preventDefault();
        this.activeIndex = (this.activeIndex - 1 + n) % n;
        this.updateActive();
      } else if (e.key === 'Enter') {
        if (this.activeIndex >= 0 && this.items[this.activeIndex]) {
          e.preventDefault();
          window.location.href = this.items[this.activeIndex].url;
        }
        // sinon : soumission normale du formulaire → page de résultats (type=article)
      } else if (e.key === 'Escape') {
        this.close();
      }
    }

    open() {
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.panel.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.activeIndex = -1;
      this.updateActive();
    }

    clear() {
      this.input.value = '';
      if (this.clearBtn) this.clearBtn.hidden = true;
      this.items = [];
      this.list.innerHTML = '';
      this.footer.innerHTML = '';
      this.close();
      this.input.focus();
    }
  }

  customElements.define('tl-blog-search', TLBlogSearch);
})();
